import React, { useEffect, useState } from "react";
import { Loader, Wand2, Quote, Lightbulb, Copy, Check, Sparkles } from "lucide-react";
import { api, friendlyAxiosError } from "../../lib/api";
import ProgressMeter from "../ProgressMeter";

export default function PitchPanel() {
  const [targetRole, setTargetRole] = useState("");
  const [audience, setAudience] = useState("");
  const [superpower, setSuperpower] = useState("");
  const [loading, setLoading] = useState(false);
  const [pitch, setPitch] = useState(null);
  const [error, setError] = useState("");
  const [copiedIdx, setCopiedIdx] = useState(null);

  // Superpower-suggest state
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [suggestErr, setSuggestErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/network/pitch/latest");
        if (data && data.id) {
          setPitch(data);
          setTargetRole(data.target_role || "");
          setAudience(data.audience || "");
          setSuperpower(data.superpower || "");
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const suggestSuperpower = async () => {
    setSuggestErr("");
    setSuggesting(true);
    try {
      const { data } = await api.post("/network/pitch/suggest-superpower");
      setSuggestion(data);
      // Auto-fill the input so the user can either keep, edit, or regenerate.
      if (data.superpower) setSuperpower(data.superpower);
    } catch (e) {
      setSuggestErr(friendlyAxiosError(e));
    } finally {
      setSuggesting(false);
    }
  };

  const generate = async () => {
    setError("");
    if (targetRole.trim().length < 2) {
      setError("Tell Resmo the role you're aiming at first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/network/pitch", {
        target_role: targetRole.trim(),
        audience: audience.trim() || null,
        superpower: superpower.trim() || null,
      });
      setPitch(data);
    } catch (e) {
      setError(friendlyAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text, idx) => {
    await navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  return (
    <div className="space-y-6" data-testid="pitch-panel">
      <div className="rm-card-static p-6 md:p-8">
        <div className="rm-label mb-1" style={{ color: "#FF6A00" }}>· What's your aim</div>
        <h2 className="font-display text-2xl uppercase mb-4">Set the target.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="rm-label block mb-1">Target role *</label>
            <input className="rm-input" placeholder="Senior PM, Head of Marketing..."
                   value={targetRole} onChange={(e) => setTargetRole(e.target.value)}
                   data-testid="pitch-target-role" />
          </div>
          <div>
            <label className="rm-label block mb-1">Audience (optional)</label>
            <input className="rm-input" placeholder="VCs, hiring managers, peers..."
                   value={audience} onChange={(e) => setAudience(e.target.value)}
                   data-testid="pitch-audience" />
          </div>
          <div className="md:col-span-2">
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className="rm-label">Your superpower (optional)</label>
              <button
                type="button"
                onClick={suggestSuperpower}
                disabled={suggesting}
                className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1 px-2 py-1"
                style={{
                  background: "linear-gradient(135deg,#FF6A00,#B45309)",
                  color: "white",
                  border: "2px solid #0F1B2D",
                  borderRadius: 5,
                  fontWeight: 700,
                  boxShadow: "2px 2px 0 0 #0F1B2D",
                }}
                data-testid="pitch-suggest-superpower-btn"
                title="Let Resmo read your resume + recent skill assessment and propose what makes you unforgettable. Free."
              >
                {suggesting ? <Loader size={11} className="animate-spin" /> : <Sparkles size={11} />}
                {suggesting ? "Resmo is reading you…" : suggestion ? "Suggest another" : "Resmo, what's mine?"}
              </button>
            </div>
            <input className="rm-input" placeholder="Translating data into product bets..."
                   value={superpower} onChange={(e) => setSuperpower(e.target.value)}
                   data-testid="pitch-superpower" />
            {suggestErr && (
              <div className="mt-2 font-mono-rm text-xs" style={{ color: "#991B1B" }}
                   data-testid="pitch-superpower-error">
                {suggestErr}
              </div>
            )}
          </div>
        </div>

        {/* Suggestion evidence card */}
        {suggestion && (
          <div
            className="mt-4 p-4 rounded-md"
            style={{ background: "#FFFBEB", border: "3px dashed #FFC145" }}
            data-testid="pitch-superpower-suggestion"
          >
            <div className="flex items-start gap-2 mb-2">
              <Sparkles size={14} style={{ color: "#92400E" }} className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#92400E", fontWeight: 700 }}>
                  Resmo sees this in you
                </div>
                <p className="font-display text-base md:text-lg leading-snug mt-1"
                   style={{ color: "#0F1B2D", fontFamily: "Georgia, serif" }}>
                  "{suggestion.superpower}"
                </p>
              </div>
            </div>
            {(suggestion.evidence || []).length > 0 && (
              <div className="mt-3">
                <div className="font-mono-rm text-[9px] uppercase tracking-widest mb-1" style={{ color: "#92400E", fontWeight: 700 }}>
                  Why Resmo says so
                </div>
                <ul className="text-sm space-y-1 list-none">
                  {suggestion.evidence.map((ev, i) => (
                    <li key={i} className="flex gap-2">
                      <span style={{ color: "#FF6A00" }}>▸</span>
                      <span>{ev}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(suggestion.alternatives || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="font-mono-rm text-[9px] uppercase tracking-widest self-center" style={{ color: "#92400E", fontWeight: 700 }}>
                  Or phrase it as:
                </span>
                {suggestion.alternatives.map((alt, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSuperpower(alt)}
                    className="font-mono-rm text-[10px] px-2 py-1"
                    style={{
                      background: "white",
                      color: "#0F1B2D",
                      border: "2px solid #0F1B2D",
                      borderRadius: 4,
                      fontWeight: 600,
                    }}
                    data-testid={`pitch-superpower-alt-${i}`}
                  >
                    {alt}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 rounded-md font-mono-rm text-sm"
               style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
               data-testid="pitch-error">
            {error}
          </div>
        )}
        <button onClick={generate} disabled={loading} className="rm-btn rm-btn-primary mt-5"
                data-testid="pitch-generate-btn">
          {loading ? <Loader size={16} className="animate-spin" /> : <Wand2 size={16} />}
          {loading ? "Resmo is writing..." : pitch ? "Re-generate (10 credits)" : "Write my pitch (10 credits)"}
        </button>
        {loading && (
          <div className="mt-3 max-w-sm">
            <ProgressMeter expectedSeconds={30} label="Writing your pitch" testid="pitch-progress-meter" />
          </div>
        )}
      </div>

      {pitch && (pitch.variants || []).length > 0 && (
        <div className="space-y-4" data-testid="pitch-results">
          {pitch.variants.map((v, i) => (
            <div key={i} className="rm-card-static p-5 md:p-6"
                 style={{
                   background: v.tone === "warm" ? "#FFFBEB"
                              : v.tone === "executive" ? "#0F1B2D" : "#FFF1E6",
                   color: v.tone === "executive" ? "white" : "#0F1B2D",
                   borderColor: "#0F1B2D",
                 }}
                 data-testid={`pitch-variant-${v.tone}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <div>
                  <div className="font-mono-rm text-[10px] uppercase tracking-widest"
                       style={{ color: v.tone === "executive" ? "#FFC145" : "#B45309", fontWeight: 700 }}>
                    · {v.tone} · {v.label}
                  </div>
                  {v.use_when && (
                    <div className="text-xs mt-1" style={{ color: v.tone === "executive" ? "#CBD5E1" : "#475569" }}>
                      {v.use_when}
                    </div>
                  )}
                </div>
                <button onClick={() => copy(v.pitch, i)} className="rm-btn text-[11px] px-3 py-1.5"
                        style={{ background: v.tone === "executive" ? "#FFC145" : "white",
                                 color: "#0F1B2D", border: "2px solid #0F1B2D", fontWeight: 700 }}
                        data-testid={`pitch-copy-${v.tone}`}>
                  {copiedIdx === i ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                </button>
              </div>
              <Quote size={18} className="opacity-30 mb-2" />
              <p className="text-base md:text-lg leading-relaxed font-display" style={{ fontFamily: "Georgia, serif" }}>
                {v.pitch}
              </p>
            </div>
          ))}

          {(pitch.rehearsal_cues || []).length > 0 && (
            <div className="rm-card-static p-5 md:p-6" style={{ background: "white" }} data-testid="pitch-rehearsal">
              <div className="rm-label mb-2" style={{ color: "#FF6A00" }}>· Rehearsal cues</div>
              <ul className="space-y-2 text-sm">
                {pitch.rehearsal_cues.map((c, i) => (
                  <li key={i} className="flex gap-2">
                    <Lightbulb size={14} className="flex-shrink-0 mt-0.5" style={{ color: "#FF6A00" }} />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {pitch.follow_up_line && (
            <div className="rm-card-static p-5 md:p-6" style={{ background: "#ECFDF5", borderColor: "#10B981" }} data-testid="pitch-followup">
              <div className="rm-label mb-1" style={{ color: "#065F46" }}>· After the pitch lands…</div>
              <p className="text-sm italic">"{pitch.follow_up_line}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
